import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import moment from "moment";
import { Link } from "react-router-dom";
import { getBlogAds, getBlogs } from "../../services/BlogService";
import Preloader from "../../Preloader";
import "./BlogsPage.css";

function getBlogTitle(blog) {
  return blog?.blog_subject || blog?.meta_title || "Blog Title";
}

const BLOG_FALLBACK_IMAGES = [
  "/kuku-assets/products/sweet/kaju katli/1 kaju katli.jpg",
  "/kuku-assets/products/dry-round-kachori/1 dry-round.jpg",
  "/kuku-assets/products/rajbhog-kachori/1 rajbhog.jpg",
  "/kuku-assets/products/dry-chapti-kachori/1 chapti kachori.jpg",
];

function groupAdsByCategory(ads = []) {
  return Object.values(
    ads.reduce((groups, ad) => {
      const categoryId = ad?.category_id || "uncategorized";

      if (!groups[categoryId]) {
        groups[categoryId] = {
          categoryId,
          title: ad?.title || "Kuku Namkeen",
          images: [],
        };
      }

      if (ad?.image) {
        groups[categoryId].images.push({
          id: ad?.id || `${categoryId}-${groups[categoryId].images.length}`,
          src: ad.image,
          alt: ad?.title || groups[categoryId].title,
        });
      }

      return groups;
    }, {})
  ).filter((group) => group.images.length > 0);
}

function BlogAdSlider({ images }) {
  const [activeIndex, setActiveIndex] = React.useState(0);

  React.useEffect(() => {
    if (images.length <= 1) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % images.length);
    }, 2800);

    return () => window.clearInterval(timerId);
  }, [images.length]);

  return (
    <div className="blogs-ad-slider">
      {images.map((image, index) => (
        <img
          key={image.id}
          src={image.src}
          alt={image.alt}
          className={`blogs-sidebar__image blogs-ad-slide${
            index === activeIndex ? " is-active" : ""
          }`}
          loading="lazy"
        />
      ))}
    </div>
  );
}

function BlogAdsSidebar({ adGroups }) {
  if (adGroups.length === 0) {
    return null;
  }

  return (
    <aside className="blogs-sidebar" aria-label="Featured categories">
      {adGroups.map((group) => (
        <div className="blogs-sidebar__block" key={group.categoryId}>
          <h2>{group.title}</h2>
          <BlogAdSlider images={group.images} />
          <Link
            to={`/category/${group.categoryId}`}
            className="blogs-sidebar__button"
          >
            View All
          </Link>
        </div>
      ))}
    </aside>
  );
}

function BlogsPage() {
  const { data: blogResponse, isLoading } = useQuery({
    queryKey: ["blogs"],
    queryFn: getBlogs,
  });
  const { data: blogAdsResponse } = useQuery({
    queryKey: ["blog-ads"],
    queryFn: getBlogAds,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoading) {
    return <Preloader />;
  }

  const blogs = blogResponse?.data || [];
  const blogAdGroups = groupAdsByCategory(blogAdsResponse?.data || []);

  return (
    <>
      <Helmet>
        <title>KUKUNAMKEEN | Blogs</title>
        <meta
          name="description"
          content="Read the latest Kuku Namkeen blogs, product stories, and brand updates."
        />
        <meta
          name="keywords"
          content="Kuku Namkeen blogs, namkeen articles, kachori stories, snacks updates"
        />
        <link rel="canonical" href="https://kukunamkeen.in/blogs" />
      </Helmet>

      <section className="blogs-page">
        <section className="dist-header-title">
          <div className="max-w-6xl mx-auto px-4">
            <h1>Blogs</h1>
          </div>
        </section>
        <img
          src="/Image/border-bottom-title.png"
          className="w-full header-title-image"
          alt=""
        />

        <section className="blogs-content">
          <div className="blogs-layout">
            <div className="blogs-main">
              {blogs.length > 0 ? (
                <div className="blogs-grid">
                  {blogs.map((blog, index) => {
                    const blogImage =
                      blog.file ||
                      BLOG_FALLBACK_IMAGES[index % BLOG_FALLBACK_IMAGES.length];

                    return (
                      <article key={blog.blog_id} className="blogs-card">
                        <img
                          src={blogImage}
                          alt={getBlogTitle(blog)}
                          className="blogs-card__image"
                          loading="lazy"
                        />

                        <div className="blogs-card__body">
                          <h2>{getBlogTitle(blog)}</h2>
                          <span>
                            {moment(blog.created_at).isValid()
                              ? moment(blog.created_at).format("MMM YYYY")
                              : ""}
                          </span>
                          <Link
                            to={`/blogs/${blog.blog_id}`}
                            className="blogs-card__link"
                          >
                            Read Full Blog
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="blogs-empty">
                  No blogs are available right now.
                </div>
              )}
            </div>

            <BlogAdsSidebar adGroups={blogAdGroups} />
          </div>
        </section>
      </section>
    </>
  );
}

export default BlogsPage;
